import { z, type ZodType } from 'zod';
import {
  ErrorCode,
  RussiaRequestCarrierBidSchema,
  RussiaRequestCarrierSchema,
  RussiaRequestSchema,
  type AddRussiaRequestBidInputWire,
  type AddRussiaRequestCarrierInput,
  type CreateRussiaRequestFromParentInput,
  type CreateRussiaRequestManualInput,
  type PatchRussiaRequestInput,
  type RussiaRequest,
  type RussiaRequestCarrier,
  type RussiaRequestCarrierBid,
  type UpdateRussiaRequestBidInput,
  type UpdateRussiaRequestCarrierInput,
} from '@magimp/shared';

import { request } from '../client.js';
import { ApiClientError } from '../errors.js';

// INVARIANT (#1180 contract): the backend stamps `meta.reason` with this
//   exact string on the no-child 404 for `GET /russia-requests/from-parent/:id`.
//   Only this exact discriminator collapses to `null` below — every other
//   404 (parent missing, hidden, generic gateway) re-throws so the caller's
//   normal error path runs. Keep this string in lock-step with the backend
//   `RussiaRequestFromParentNotFoundError`.
const RUSSIA_REQUEST_FROM_PARENT_NOT_FOUND_REASON = 'russia_request_from_parent_not_found';

export function createRussiaRequestManual(
  input: CreateRussiaRequestManualInput,
): Promise<RussiaRequest> {
  return request<RussiaRequest>(
    '/russia-requests/manual',
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

/**
 * @aiPurpose GET /russia-requests/from-parent/:requestId. Lookup-only
 *   probe used by the #1180 Russia-rate confirm-only-if-missing flow.
 *   Returns the existing snapshot when authorized; resolves to `null`
 *   on the exact no-child 404 envelope (and ONLY that envelope) so the
 *   drawer can decide whether to prompt for creation. All other 4xx/5xx
 *   re-throw and flow through the standard error toast path.
 * @aiSecurity backend gate is `russiaRequests.manage` + parent-ownership
 *   (admin / `russiaRequests.scope.all` / `parent.managerId`). The web
 *   wrapper does not relax this — it only collapses the no-child
 *   discriminator.
 */
export async function getRussiaRequestFromParent(
  parentRequestId: string,
): Promise<RussiaRequest | null> {
  try {
    return await request<RussiaRequest>(
      `/russia-requests/from-parent/${encodeURIComponent(parentRequestId)}`,
      { method: 'GET' },
      {},
      RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
    );
  } catch (err) {
    // INVARIANT (#1180): map ONLY the typed no-child envelope to `null`.
    //   `RussiaRequestParentNotFoundError` and `RussiaRequestNotFoundError`
    //   also return 404 NOT_FOUND but lack this `meta.reason` —
    //   collapsing them would mis-route a missing-parent into a stray
    //   create-confirmation prompt.
    if (
      err instanceof ApiClientError &&
      err.httpStatus === 404 &&
      err.code === ErrorCode.NOT_FOUND &&
      err.meta?.['reason'] === RUSSIA_REQUEST_FROM_PARENT_NOT_FOUND_REASON
    ) {
      return null;
    }
    throw err;
  }
}

/**
 * @aiPurpose POST /russia-requests/from-parent/:requestId. Idempotent on
 *   `parentRequestId`: a repeat call with the same parent returns the
 *   existing RussiaRequest unchanged. The China-side "Russia-rate"
 *   button (#707) relies on this contract.
 */
export function createRussiaRequestFromParent(
  parentRequestId: string,
  input: CreateRussiaRequestFromParentInput = {},
): Promise<RussiaRequest> {
  return request<RussiaRequest>(
    `/russia-requests/from-parent/${encodeURIComponent(parentRequestId)}`,
    {
      method: 'POST',
      body: JSON.stringify(input),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

export function patchRussiaRequest(
  id: string,
  body: PatchRussiaRequestInput,
): Promise<RussiaRequest> {
  return request<RussiaRequest>(
    `/russia-requests/${encodeURIComponent(id)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

/**
 * @aiPurpose POST /russia-requests/:id/carriers. Adds a catalog carrier
 *   to the Russia request's carrier set in `PENDING` (no bids).
 *   Service-side guard: parent must be `IN_WORK` and the catalog row
 *   must be `active=true`; the `(russiaRequestId, carrierId)` pair is
 *   UNIQUE. Duplicate surfaces as `DOMAIN_CONFLICT` (409); missing/
 *   inactive catalog row surfaces as `NOT_FOUND` (404).
 */
export function addRussiaRequestCarrier(
  russiaRequestId: string,
  body: AddRussiaRequestCarrierInput,
): Promise<RussiaRequestCarrier> {
  return request<RussiaRequestCarrier>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestCarrierSchema as unknown as ZodType<RussiaRequestCarrier>,
  );
}

/**
 * @aiPurpose POST /russia-requests/:id/carriers/:carrierId/bids. Adds
 *   one `RussiaRequestCarrierBid` row in either REQUEST scope
 *   (legacy/default whole-request bid) or START_CITY scope (per
 *   departure-city rate). Body `scope` defaults to `REQUEST`.
 *   Server-side guards (#1580):
 *   - parent must be visible + IN_WORK (else 404 / 409 INVALID_TRANSITION)
 *   - carrier must belong to this parent + not REJECTED
 *     (else 404 / 409 INVALID_TRANSITION)
 *   - REQUEST: plain insert, no per-carrier primary required.
 *   - START_CITY: requires `scopeRefId` (one of the parent's start
 *     cities — else 404 NOT_FOUND); no existing REQUEST primary needed.
 *     UPSERTs on the unique `(carrier, scopeRefId)` tuple, so re-posting
 *     the same `(carrier, start-city)` re-prices the bid in place.
 */
export function addRussiaRequestBid(
  russiaRequestId: string,
  russiaRequestCarrierId: string,
  body: AddRussiaRequestBidInputWire,
): Promise<RussiaRequestCarrierBid> {
  return request<RussiaRequestCarrierBid>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers/${encodeURIComponent(
      russiaRequestCarrierId,
    )}/bids`,
    {
      method: 'POST',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestCarrierBidSchema as unknown as ZodType<RussiaRequestCarrierBid>,
  );
}

/**
 * @aiPurpose POST /russia-requests/:id/duplicate (#2120). Duplicates an
 *   existing Russia request (any status) into a new DRAFT owned by the actor;
 *   the response is the new DRAFT's `RussiaRequest` wire shape and the caller
 *   navigates to it. No request body — v1 has no server-side idempotency
 *   (single-flight is enforced client-side by disabling the action in flight).
 */
export function duplicateRussiaRequest(id: string): Promise<RussiaRequest> {
  return request<RussiaRequest>(
    `/russia-requests/${encodeURIComponent(id)}/duplicate`,
    { method: 'POST' },
    {},
    RussiaRequestSchema as unknown as ZodType<RussiaRequest>,
  );
}

export function deleteRussiaRequest(id: string): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(id)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

/**
 * @aiPurpose PATCH /russia-requests/:id/carriers/:carrierId. Updates the
 *   snapshot fields (`name` / `contacts`) on the join row only — the
 *   parent `RussiaCarrier` directory entry is NEVER touched. Empty
 *   bodies are rejected at the schema layer with 400.
 */
export function updateRussiaRequestCarrier(
  russiaRequestId: string,
  russiaRequestCarrierId: string,
  body: UpdateRussiaRequestCarrierInput,
): Promise<RussiaRequestCarrier> {
  return request<RussiaRequestCarrier>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers/${encodeURIComponent(
      russiaRequestCarrierId,
    )}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestCarrierSchema as unknown as ZodType<RussiaRequestCarrier>,
  );
}

/**
 * @aiPurpose DELETE /russia-requests/:id/carriers/:carrierId. Hard-deletes
 *   the join row; FK cascade removes the carrier's bids. The directory
 *   `RussiaCarrier` row is NOT touched.
 */
export function deleteRussiaRequestCarrier(
  russiaRequestId: string,
  russiaRequestCarrierId: string,
): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers/${encodeURIComponent(
      russiaRequestCarrierId,
    )}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}

/**
 * @aiPurpose PATCH /russia-requests/:id/carriers/:carrierId/bids/:bidId.
 *   In-place update of `price` / `comment` / `validityDays` (any subset).
 *   `quotedAt` is refreshed server-side so `expiresAt` re-derives from
 *   the moment of the edit. Empty bodies are rejected with 400.
 */
export function updateRussiaRequestBid(
  russiaRequestId: string,
  russiaRequestCarrierId: string,
  bidId: string,
  body: UpdateRussiaRequestBidInput,
): Promise<RussiaRequestCarrierBid> {
  return request<RussiaRequestCarrierBid>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers/${encodeURIComponent(
      russiaRequestCarrierId,
    )}/bids/${encodeURIComponent(bidId)}`,
    {
      method: 'PATCH',
      body: JSON.stringify(body),
      headers: { 'content-type': 'application/json' },
    },
    {},
    RussiaRequestCarrierBidSchema as unknown as ZodType<RussiaRequestCarrierBid>,
  );
}

/**
 * @aiPurpose DELETE /russia-requests/:id/carriers/:carrierId/bids/:bidId.
 *   Hard-deletes a single bid; the carrier row stays.
 */
export function deleteRussiaRequestBid(
  russiaRequestId: string,
  russiaRequestCarrierId: string,
  bidId: string,
): Promise<void> {
  return request<void>(
    `/russia-requests/${encodeURIComponent(russiaRequestId)}/carriers/${encodeURIComponent(
      russiaRequestCarrierId,
    )}/bids/${encodeURIComponent(bidId)}`,
    { method: 'DELETE' },
    {},
    z.void(),
  );
}
